import React from "react";
import { ShieldCheck, MapPin, FileText, IndianRupee, Camera, CircleCheck, CircleDot } from "lucide-react";
import { fmtDate } from "@/lib/loop";

const STAGES = ["Listed", "Owner Verified", "Location Verified", "Documents Uploaded", "Site Visit", "Updated", "Sold"];

function stageIndex(p) {
  let i = 1;
  if (p.owner_status === "verified") i = 2;
  if (p.location_status === "verified") i = 3;
  if ((p.documents || []).some((d) => d.status !== "not_provided")) i = Math.max(i, 4);
  if (p.status === "sold" || p.status === "rented") i = 7;
  return i;
}

const Row = ({ Icon, label, value, tone = "text-slate-200" }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-white/5">
    <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
      <Icon className="w-3.5 h-3.5 text-[#C5A059]" /> {label}
    </span>
    <span className={`text-sm font-medium text-right ${tone}`}>{value}</span>
  </div>
);

export default function PropertyPassport({ p }) {
  const idx = stageIndex(p);
  const capTone = (ok, pend) =>
    ok ? "text-emerald-400" : pend ? "text-amber-400" : "text-slate-400";

  return (
    <div data-testid="property-passport" className="bg-[#070C1E] border border-[#C5A059]/30 rounded-3xl p-6 sm:p-8 relative shadow-2xl overflow-hidden">
      <div className="absolute inset-0 watermark-grid opacity-40 pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Property Passport
            </div>
            <div className="mt-2 font-mono-loop text-sm tracking-widest text-[#E6C687] bg-[#C5A059]/10 inline-block px-3 py-1.5 rounded-md border border-[#C5A059]/30">
              {p.loop_id}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Property Status</div>
            <div className="text-lg font-bold capitalize text-white">{p.status.replace("_", " ")}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-x-10">
          <div>
            <Row Icon={CircleDot} label="Last Verified" value={fmtDate(p.last_verified)} />
            <Row Icon={ShieldCheck} label="Owner Status"
              value={p.owner_status === "verified" ? "Identity Verified" : "Pending"}
              tone={capTone(p.owner_status === "verified", true)} />
            <Row Icon={MapPin} label="Location"
              value={p.location_status === "verified" ? "Verified" : "Approximate"}
              tone={capTone(p.location_status === "verified", true)} />
          </div>
          <div>
            <Row Icon={FileText} label="Document Status" value={p.document_status}
              tone={capTone(p.document_status === "complete", p.document_status === "partial")} />
            <Row Icon={IndianRupee} label="Price Source"
              value={p.price_source === "agent" ? "Agent supplied" : "Owner supplied"} />
            <Row Icon={Camera} label="Media"
              value={`${(p.images || []).length} date-stamped items`} />
          </div>
        </div>

        {/* Verification timeline */}
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-4">Verification Timeline</div>
          <div className="flex flex-wrap gap-2" data-testid="passport-verification-timeline">
            {STAGES.map((s, i) => {
              const done = i < idx;
              const current = i === idx - 1;
              return (
                <div key={s}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border
                    ${done ? "bg-[#C5A059]/15 text-[#E6C687] border-[#C5A059]/40"
                           : "bg-white/5 text-slate-500 border-white/10"}`}>
                  {done ? <CircleCheck className="w-3.5 h-3.5" /> : <CircleDot className="w-3.5 h-3.5" />}
                  {s}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
